import Button from '@components/Button';
import ModalUserDetails from '@components/ModalUserDetails';
import QuestionDemographics from '@components/QuestionDemographics';
import QuestionInline from '@components/QuestionInline';
import { SelectedAnswersType, SurveyStartType } from '@data/types';
import useQuestionnaireStore from '@store/questionnaire';
import classNames from 'classnames';
import React, { useEffect, useState } from 'react';
import { hash } from 'utilities/helper';

import CtaForm from './CtaForm';

interface Props extends SurveyStartType {
  as?: keyof JSX.IntrinsicElements;
}

const SurveyStart: React.FC<Props> = ({
  as: Component = 'div',
  questions,
  htmlContent,
}) => {
  const [selectedAnswers, setSelectedAnswers] = useState<SelectedAnswersType[]>(
    []
  );
  const [displayModal, setDisplayModal] = useState<boolean>(false);

  const { questionIndexStore, setAnswerStore, pillarsStore } =
    useQuestionnaireStore();

  const disableButton = () => {
    // Loop through all of the potential answers, if any undefined disable the button
    for (let i = 0; i < questions.length; i++) {
      if (selectedAnswers[i] === null || selectedAnswers[i] === undefined) {
        return true;
      }
    }

    return false;
  };

  const buttonOnClick = (): void => {
    // Save answer
    setAnswerStore(0, questionIndexStore, selectedAnswers);

    // Display User details modal
    setDisplayModal(!displayModal);
  };

  // Populte answers with pre-selections, index is 0 as this is the first page
  useEffect(() => {
    const pillar = pillarsStore?.[0];
    const answers = pillar?.answers?.[0];

    if (answers) {
      // Sort array by questionIndex & add to selected answers
      setSelectedAnswers([
        ...answers.value.sort((a, b) => a.questionIndex - b.questionIndex),
      ]);
    }
  }, []);

  return (
    <>
      <CtaForm
        htmlContent="<h2>Already taken part? Retrieve your results</h2>"
        supplementaryHtmlContent="<p>Please come back to review your saved assessment regularly – we’ll update it as our data grows.</p>"
        className="lg:-mx-[100vw] lg:w-auto -mt-5 md:mt-0"
        submittingText="Retrieving..."
      />
      <Component className="w-full">
        <div className="max-w-innerGrid mx-auto overflow-hidden px-4 py-12 xl:px-0 xl:py-16 relative z-50">
          {questions.map((item, index) => {
            // Demographics
            if (index == 0)
              return (
                <QuestionDemographics
                  {...item}
                  htmlContent={htmlContent}
                  key={hash(item)}
                  selectedAnswers={selectedAnswers}
                  setSelectedAnswers={setSelectedAnswers}
                  index={index}
                  id={`question-${index + 1}`}
                />
              );

            return (
              <QuestionInline
                {...item}
                key={hash(item)}
                selectedAnswers={selectedAnswers}
                setSelectedAnswers={setSelectedAnswers}
                index={index}
                id={`question-${index + 1}`}
              />
            );
          })}

          <div className="-mt-8 lg:-mt-16 relative">
            <Button
              type="button"
              title="Start survey"
              onClick={buttonOnClick}
              disabled={disableButton()}
              className={classNames('!bg-red mt-4 mb-8 !text-white', {
                'opacity-50': disableButton(),
              })}
            />
          </div>
        </div>
      </Component>
      {displayModal && (
        <ModalUserDetails
          setDisplayModal={setDisplayModal}
          displayModal={displayModal}
        />
      )}
    </>
  );
};

export default SurveyStart;
